import styled, { css, keyframes } from 'styled-components'

import { mq, sizes } from '../../theme'
import Highlight from '../Highlight'
import ActionButton from '../ActionButton'
import A from '../A'
import Collapsable from '../Collapsable'
import Stepper from '../Stepper'
import Icon from '../Icon'
import Slider from '../FormElements/Slider'
import { WysiwygContainer } from '../WysiwygRenderer/styled'

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`

export const Inner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

export const OnboardingStepper = styled(Stepper)`
  margin-top: -6rem;
  @media ${mq(sizes.desktop)} {
    margin-top: -7.5rem;
  }
`

export const OnboardingCollapsable = styled(Collapsable)`
  .collapsable {
    margin-top: 0 !important;
  }

  &:last-of-type {
    padding-bottom: 8rem;
  }

  &.isAlone {
    margin-top: -7.5rem;
  }

  &:not(.isAlone) {
    &:not(:first-child) {
      margin-top: 0rem !important;
    }
  }
`

export const Step = styled.div<{ bigMargin?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;

  ${({ bigMargin }) =>
    bigMargin &&
    css`
      margin: 5rem 0 3rem 0;
    `}
`

export const BackContainer = styled.div`
  position: absolute;
  @media ${mq(sizes.desktop)} {
    top: 8rem;
  }
`

export const BackButton = styled(ActionButton)`
  display: none;

  @media ${mq(sizes.desktop)} {
    display: flex;
  }
`
export const BackButtonMobile = styled(ActionButton)`
  display: flex;

  @media ${mq(sizes.desktop)} {
    display: none;
  }
`

export const QuitOnboarding = styled(A)`
  margin-top: 2rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  max-width: 60rem;
  ${({ theme }) => theme.textStyles.text_16};

  &:hover {
    color: ${({ theme }) => theme.colors.greenApple};
    text-decoration-color: ${({ theme }) => theme.colors.greenApple};
  }
`

export const StepHighlight = styled(Highlight)`
  ${({ theme }) => theme.textStyles.text_bold_22};
  text-align: center;
  margin: 0 3.5rem;

  & span {
    padding: 0.2rem 0.8rem;
  }

  @media ${mq(sizes.desktop)} {
    ${({ theme }) => theme.textStyles.titleH1_26};
    max-width: 70rem;
    margin: 0;

    & span {
      line-height: 1.6;
    }
  }
`

export const Title = styled.span`
  ${({ theme }) => theme.textStyles.text_bold_18};
  text-align: center;

  @media ${mq(sizes.desktop)} {
    ${({ theme }) => theme.textStyles.text_bold_32};
  }
`

export const Legend = styled.div`
  padding: 2rem 0;
  ${({ theme }) => theme.textStyles.text_bold_16};
  font-style: italic;
  text-align: center;
`

export const StepTexts = styled.div<{ light?: boolean }>`
  max-width: 61rem;
  margin-bottom: 4rem;
  line-height: 2.6rem;

  &:last-child {
    margin-top: 4rem;
  }

  p {
    ${({ theme, light }) =>
      light ? theme.textStyles.text_16 : theme.textStyles.text_medium_16};
    text-align: center;

    &.bold {
      ${({ theme }) => theme.textStyles.text_bold_16};
    }

    &.italic {
      ${({ theme }) => theme.textStyles.text_16};
      font-style: italic;
    }

    @media ${mq(sizes.desktop)} {
      ${({ theme, light }) =>
        light ? theme.textStyles.text_16 : theme.textStyles.text_medium_16};
      text-align: center;

      &.bold {
        ${({ theme }) => theme.textStyles.text_bold_16};
      }

      &.italic {
        ${({ theme }) => theme.textStyles.text_16};
        font-style: italic;
      }
    }

    &:not(:first-child) {
      margin-top: 0;
    }
  }
  &.-intersitial {
    margin-bottom: 0;
  }
  &.-intersitial p:not(:first-child) {
    @media ${mq(sizes.desktop)} {
      margin-top: 2.4rem;
    }
  }
  &.-congratulations p:not(:first-child) {
    @media ${mq(sizes.desktop)} {
      margin-top: 0.5rem;
    }
  }
  &.-generation p {
    @media ${mq(sizes.desktop)} {
      max-width: 33rem;
    }
  }
`

export const StepImage = styled.div<{ hasImage?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 19rem;
  margin-bottom: 1.5rem;

  ${({ hasImage }) =>
    !hasImage &&
    css`
      height: 13rem;
    `}

  &:not(:first-child) {
    margin-top: 1.5rem;
  }

  @media ${mq(sizes.tablet)} {
    width: 20rem;
    ${({ hasImage }) =>
      !hasImage &&
      css`
        height: 16.5rem;
      `}
    margin-bottom: 1rem;

    &:not(:first-child) {
      margin-top: 2rem;
    }
  }
`

export const BottomButtons = styled.div<{ smallMargin?: boolean }>`
  display: flex;
  flex-direction: column-reverse;
  margin-top: 3rem;

  & > * {
    margin: 1rem 0;
  }

  @media ${mq(sizes.desktop)} {
    flex-direction: row;
    margin-top: ${({ smallMargin }) => (smallMargin ? '3rem' : '6rem')};

    & > * {
      margin: 0 0.75rem;
    }
  }
`

export const OnboardingSlider = styled(Slider)<{ count: number }>`
  max-width: 57rem;
  @media ${mq(sizes.desktop)} {
    width: ${({ count }) => count * 12}rem;
  }

  .rc-slider-mark-text {
    ${({ theme }) => theme.textStyles.text_bold_condensed_14};
    white-space: normal;
    text-align: left;
    @media ${mq(sizes.desktop)} {
      ${({ theme }) => theme.textStyles.text_bold_condensed_14};
      text-align: center;
      width: ${({ count }) => 127 / count}%;
      max-width: 15rem;
    }
  }
`

export const GenerationIcon = styled(Icon)`
  display: flex;
  width: 4.5rem;
  height: 4.5rem;
  fill: ${({ theme }) => theme.colors.greenApple};
  color: ${({ theme }) => theme.colors.greenApple};
  margin: 4rem 0 2rem 0;
  animation: ${rotate} 2s linear infinite;

  @media ${mq(sizes.desktop)} {
    width: 5.5rem;
    height: 5.5rem;
    margin-top: 3rem 0 0 0;
  }
`

export const TipContainer = styled.div<{ $needMargin: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 73rem;
  margin: ${({ $needMargin }) =>
    $needMargin ? '7rem 0 0 0' : '3rem auto 0 auto'};
  background-color: ${({ theme }) => theme.colors.greyf5};
  padding: 2rem 1.5rem;
`

export const TipTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  & span {
    ${({ theme }) => theme.textStyles.text_bold_18}

    @media ${mq(sizes.desktop)} {
      ${({ theme }) => theme.textStyles.text_bold_22}
    }
  }
`

export const TitleLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

export const TipIcon = styled(Icon)`
  display: block;
  ${({ icon }) =>
    icon === 'pin'
      ? css`
          width: 2.5rem;
          height: 2.5rem;
        `
      : css`
          width: 2.1rem;
          height: 3.1rem;
        `}
  fill: ${({ theme }) => theme.colors.greenApple};
  color: ${({ theme }) => theme.colors.greenApple};
  margin-right: 1.2rem;
  margin-left: 0.2rem;

  @media ${mq(sizes.desktop)} {
    ${({ icon }) =>
      icon === 'pin'
        ? css`
            width: 3.2rem;
            height: 3.2rem;
            margin-right: 1.5rem;
          `
        : css`
            width: 2.1rem;
            height: 3.1rem;
            margin-right: 2.7rem;
          `}
  }
`

export const TipChevron = styled(Icon)<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border: 1px solid ${({ theme }) => theme.colors.greyCE};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.pureWhite};
  cursor: pointer;
  & svg {
    width: 2rem;
    height: 2rem;
    color: ${({ theme }) => theme.colors.pureBlack};
    transform: ${({ $isOpen }) =>
      $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    /* transition: all 0.3s ease-in-out; */
  }
`

export const TipContent = styled(WysiwygContainer)`
  border-left: ${({ theme }) => `0.5rem solid ${theme.colors.greenMint}`};
  margin-left: 1rem;
  padding-left: 2.5rem;
  max-width: 100%;
  ${({ theme }) => theme.textStyles.text_16};
  transition: all ease-in-out 0.5s;

  max-height: 0;
  overflow: hidden;

  &.open {
    max-height: 30rem;
    margin-top: 1.5rem;
  }

  @media ${mq(sizes.desktop)} {
    margin-left: 1rem;
    padding-left: 3.5rem;
    max-width: 65rem;
  }
`

export const PreloadImagesContainer = styled.div`
  position: absolute;
  pointer-events: none;
  overflow: hidden;
  height: 300px;
  width: 300px;
  opacity: 0;
  * {
    position: absolute !important;
    inset: 0 0 0 0;
  }
`
